import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
} from "../../components/ui/pagination";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { cn } from "../../lib/utils";
import { ChevronLeft, ChevronRight, Copy, Eye, ExternalLink, Search } from "lucide-react";
import { formatCurrencyINRNoDecimals } from "../../lib/enhanced-currency-utils";
import { PurchaseOrder } from "../../types/purchaseOrder";

interface POTableProps {
  purchaseOrders: PurchaseOrder[];
  onViewDetails: (po: PurchaseOrder) => void;
  onDuplicatePO?: (po: PurchaseOrder) => void;
  pagination?: {
    totalRecords: number;
    currentPage: number;
    pageSize: number;
    totalPages: number;
  };
  onPageChange?: (page: number, pageSize: number) => void;
  loading?: boolean;
}

const statusStyles: Record<string, string> = {
  pending:
    "bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-600 hover:text-white",
  approved:
    "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white",
  completed:
    "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white",
  delivered:
    "bg-emerald-50 text-emerald-600 border-emerald-100 hover:bg-emerald-600 hover:text-white",
  partial:
    "bg-orange-50 text-orange-600 border-orange-100 hover:bg-orange-600 hover:text-white",
  draft:
    "bg-amber-50 text-amber-600 border-amber-100 hover:bg-amber-600 hover:text-white",
};

const formatDateOnly = (dateString: string | null | undefined): string => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toISOString().split("T")[0];
  } catch {
    return dateString;
  }
};

export const POTable = ({
  purchaseOrders,
  onViewDetails,
  onDuplicatePO,
  pagination,
  onPageChange,
  loading = false,
}: POTableProps) => {
  const totalPages = pagination?.totalPages ?? 1;

  return (
    <div className="overflow-hidden rounded-[20px] border border-slate-200 bg-white">
      <div className="overflow-x-auto">
        <Table className="w-full table-auto">
          <TableHeader>
            <TableRow className="border-b border-slate-200 bg-slate-50/70 hover:bg-slate-50/70">
              <TableHead className="w-[120px] py-3 pl-4 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                PO ID
              </TableHead>
              <TableHead className="w-[120px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Created At
              </TableHead>
              <TableHead className="w-[120px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Approved At
              </TableHead>
              <TableHead className="w-[120px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Supplier Name
              </TableHead>
              <TableHead className="w-[160px] px-2 text-left align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Customer Name
              </TableHead>
              <TableHead className="w-[100px] px-2 text-center align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Total Asset
              </TableHead>
              <TableHead className="w-[96px] px-2 text-right align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Amount
              </TableHead>
              <TableHead className="w-[88px] px-2 text-right align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Paid
              </TableHead>
              <TableHead className="w-[70px] px-2 text-center align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Delivered
              </TableHead>
              <TableHead className="w-[100px] px-2 text-center align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
                Status
              </TableHead>
              <TableHead className="sticky right-0 z-20 min-w-[100px] bg-slate-50/70 pr-4 text-center align-middle text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} className="py-16">
                  <div className="flex items-center justify-center">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#3f8b98]" />
                  </div>
                </TableCell>
              </TableRow>
            ) : purchaseOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} className="py-20">
                  <div className="flex flex-col items-center justify-center text-slate-400">
                    <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-50">
                      <Search className="h-8 w-8 opacity-20" />
                    </div>
                    <p className="text-sm font-bold uppercase tracking-wider text-slate-500">
                      No purchase orders found
                    </p>
                    <p className="mt-1 text-[11px]">Try adjusting your search query</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              purchaseOrders.map((po) => {
                const totalAssets = po.lines.reduce((acc, line) => acc + line.qty, 0);
                const totalAmount = po.lines.reduce((acc, line) => {
                  const subtotal = line.unitPrice * line.qty;
                  const tax = (subtotal * (line.taxRate || 0)) / 100;
                  return acc + subtotal + tax;
                }, 0);

                return (
                  <TableRow
                    key={po.purchaseOrderCode}
                    className="group border-b border-slate-100 hover:bg-slate-50/40"
                  >
                    <TableCell className="w-[180px] py-3.5 pl-4 align-middle">
                      <button
                        onClick={() => onViewDetails(po)}
                        className="inline-flex items-center gap-1.5 text-left text-[12px] font-semibold leading-5 text-[#2f93b4] transition-colors hover:text-[#25788f]"
                      >
                        <span className="break-words">{po.purchaseOrderCode}</span>
                        <ExternalLink className="h-3 w-3 text-slate-400" />
                      </button>
                    </TableCell>
                    <TableCell className="w-[84px] px-2 py-3.5 align-middle text-[12px] font-medium leading-5 text-slate-600">
                      <span className="block break-words">{formatDateOnly(po.createdAt)}</span>
                    </TableCell>
                    <TableCell className="w-[84px] px-2 py-3.5 align-middle text-[12px] font-medium leading-5 text-slate-600">
                      <span className="block break-words">
                        {po.approvedAt ? formatDateOnly(po.approvedAt) : "-"}
                      </span>
                    </TableCell>
                    <TableCell className="w-[120px] px-2 py-3.5 align-middle text-[12px] font-medium leading-5 text-slate-700">
                      <span className="block max-w-[150px] truncate text-ellipsis whitespace-nowrap">
                        {po.supplierTradeName || po.supplierName}
                      </span>
                    </TableCell>
                    <TableCell className="w-[160px] px-2 py-3.5 align-middle text-[12px] font-medium leading-5 text-slate-700">
                      <span className="block max-w-[150px] truncate text-ellipsis whitespace-nowrap">
                        {po.customerBrandName || po.customerName}
                      </span>
                    </TableCell>
                    <TableCell className="w-[68px] px-2 py-3.5 text-center align-middle text-[12px] font-semibold leading-5 text-slate-700">
                      {totalAssets}
                    </TableCell>
                    <TableCell className="w-[96px] px-2 py-3.5 text-right align-middle text-[12px] font-semibold leading-5 text-slate-800">
                      <span className="block whitespace-nowrap">
                        {formatCurrencyINRNoDecimals(totalAmount)}
                      </span>
                    </TableCell>
                    <TableCell className={cn(
                      "w-[88px] px-2 py-3.5 text-right align-middle text-[12px] font-medium leading-5",
                      Number(po.paidAmount) > 0 ? "text-slate-700" : "text-slate-400"
                    )}>
                      <span className="block whitespace-nowrap">
                        {formatCurrencyINRNoDecimals(po.paidAmount)}
                      </span>
                    </TableCell>
                    <TableCell className="w-[70px] px-2 py-3.5 text-center align-middle text-[12px] font-semibold leading-5">
                      <span
                        className={cn(
                          Number(po.deliveredAssets) === totalAssets && totalAssets > 0
                            ? "text-emerald-600"
                            : Number(po.deliveredAssets) > 0
                              ? "text-amber-600"
                              : "text-slate-400"
                        )}
                      >
                        {po.deliveredAssets}
                      </span>
                    </TableCell>
                    <TableCell className="w-[100px] px-2 py-3.5 align-middle">
                      <Badge
                        className={cn(
                          "inline-flex min-w-[98px] justify-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold shadow-none transition-colors duration-200",
                          statusStyles[po.status.toLowerCase()] || statusStyles.pending
                        )}
                      >
                        {po.status.toUpperCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="sticky right-0 z-10 min-w-[100px] bg-white pr-4 py-3.5 text-center align-middle shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)] group-hover:bg-slate-50/40">
                      <div className="flex items-center justify-center gap-1">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => onViewDetails(po)}
                          title="View purchase order"
                          aria-label="View purchase order"
                          className="h-8 w-8 shrink-0 rounded-lg border-slate-200 bg-white text-slate-600 shadow-none transition-colors hover:bg-slate-50 hover:text-[#3f8b98]"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {onDuplicatePO ? (
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => onDuplicatePO(po)}
                            title="Duplicate purchase order"
                            aria-label="Duplicate purchase order"
                            className="h-8 w-8 shrink-0 rounded-lg border-slate-200 bg-white text-[#3f8b98] shadow-none transition-colors hover:bg-[#3f8b98]/10 hover:text-[#357a85]"
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && totalPages > 1 && (
        <div className="flex flex-col gap-3 border-t border-slate-200 bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5 sm:py-4">
          <div className="text-[12px] font-medium text-slate-500 sm:text-[13px]">
            Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to{" "}
            {Math.min(
              pagination.currentPage * pagination.pageSize,
              pagination.totalRecords
            )}{" "}
            of {pagination.totalRecords} entries
          </div>

          <Pagination className="mx-0 w-auto justify-end">
            <PaginationContent className="gap-1.5">
              <PaginationItem>
                <button
                  type="button"
                  onClick={() =>
                    onPageChange?.(Math.max(1, pagination.currentPage - 1), pagination.pageSize)
                  }
                  className={cn(
                    "inline-flex h-8 items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900",
                    pagination.currentPage === 1 && "pointer-events-none opacity-50"
                  )}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Prev
                </button>
              </PaginationItem>

              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (pagination.currentPage <= 3) pageNum = i + 1;
                else if (pagination.currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = pagination.currentPage - 2 + i;
                }

                return (
                  <PaginationItem key={pageNum}>
                    <PaginationLink
                      onClick={() => onPageChange?.(pageNum, pagination.pageSize)}
                      isActive={pagination.currentPage === pageNum}
                      className={cn(
                        "flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border p-0 text-[13px] font-bold transition-colors",
                        pagination.currentPage === pageNum
                          ? "border-[#3f8b98] bg-[#3f8b98] text-white hover:bg-[#3f8b98] hover:text-white"
                          : "border-slate-200 text-slate-600 hover:bg-slate-50"
                      )}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}

              <PaginationItem>
                <button
                  type="button"
                  onClick={() =>
                    onPageChange?.(Math.min(totalPages, pagination.currentPage + 1), pagination.pageSize)
                  }
                  className={cn(
                    "inline-flex h-8 items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 text-[12px] font-medium text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900",
                    pagination.currentPage === totalPages && "pointer-events-none opacity-50"
                  )}
                >
                  Next
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
};
